import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Label,
  Dropdown,
  Option,
  Field,
  Switch,
  Tab,
  TabList,
  Checkbox,
  Radio,
  RadioGroup,
  Text
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { ReadOnlyFieldset } from "../GenericComponent/ReadOnlyFieldset";
import { useSelector } from "react-redux";
import "../CSS/AgentProfileForm.css";
import { REGIONS } from '../GenericComponent/GenericData/region'
import InfoButton from "../GenericComponent/InfoButton";

const AgentProfileForm = (props) => {
  // Reçu du parent : il connaît la permission, ce formulaire ne connaît que ses champs.
  const readOnly = props.readOnly === true;
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [selectedTab, setSelectedTab] = useState("general");

  // Get callflows from Redux
  const callFlows = useSelector(state => state.reducer.CALL_FLOWS) || {};

  // Convert callflows object/array to array format
  const getCallFlowsArray = () => {
    if (!callFlows) return [];

    // If it's already an array
    if (Array.isArray(callFlows)) {
      return callFlows;
    }

    // If it's an object, convert to array
    if (typeof callFlows === 'object') {
      return Object.values(callFlows);
    }

    return [];
  };

  const callFlowsArray = getCallFlowsArray();

  // ---------------------------------------------------------------------------
  // OBO
  // ---------------------------------------------------------------------------
  // `OBO` reste un OBJET { flowId: { displayName, isDefault? } } : c'est la forme que lit déjà
  // l'Agent Console, on ne la change pas.
  //
  // Le flux PAR DÉFAUT est celui placé en PREMIÈRE position de l'objet. C'est le contrat existant :
  // le DialPad présélectionne `Object.entries(OBO)[0]` (AgentConsole/src/components/Search/DialPad.js).
  // Cosmos stocke le document JSON tel quel, l'ordre des clés survit donc à l'aller-retour.
  //
  // On écrit EN PLUS `isDefault: true` sur cette première entrée. L'ordre seul suffirait au
  // fonctionnement, mais il est invisible et fragile à relire : ce marqueur permet à ce formulaire
  // de réafficher le bon choix de façon explicite, et laisse la possibilité de fiabiliser plus tard
  // l'Agent Console sans dépendre d'un ordre de clés.
  const oboEntries = useMemo(
    () => Object.entries(props.profile?.OBO || {}),
    [props.profile?.OBO]
  );
  const selectedFlowIds = useMemo(() => oboEntries.map(([id]) => id), [oboEntries]);
  const defaultFlowId = useMemo(() => {
    const flagged = oboEntries.find(([, value]) => value?.isDefault);
    // Repli sur la 1ʳᵉ position pour les profils enregistrés avant l'introduction du marqueur.
    return flagged ? flagged[0] : (oboEntries[0]?.[0] ?? null);
  }, [oboEntries]);

  // Reconstruit l'objet OBO avec le flux par défaut en tête.
  const buildOBO = (flowIds, wantedDefaultId) => {
    const defaultId = flowIds.includes(wantedDefaultId) ? wantedDefaultId : flowIds[0];
    const ordered = defaultId
      ? [defaultId, ...flowIds.filter(id => id !== defaultId)]
      : flowIds;

    const obo = {};
    ordered.forEach((flowId, index) => {
      const flow = callFlowsArray.find(f => f.id === flowId);
      obo[flowId] = {
        displayName: flow?.displayName || flowId,
        // Le 1er est le défaut, y compris quand l'utilisateur n'a rien choisi explicitement :
        // c'est de toute façon celui que l'Agent Console présélectionnera.
        ...(index === 0 ? { isDefault: true } : {})
      };
    });
    return obo;
  };

  const handleFlowToggle = (flowId, checked) => {
    const nextIds = checked
      ? [...selectedFlowIds, flowId]
      : selectedFlowIds.filter(id => id !== flowId);

    props.setProfile({
      ...props.profile,
      OBO: buildOBO(nextIds, defaultFlowId)
    });
  };

  const handleDefaultFlowChange = (flowId) => {
    props.setProfile({
      ...props.profile,
      OBO: buildOBO(selectedFlowIds, flowId)
    });
  };

  // ---------------------------------------------------------------------------
  // Enregistrement
  // ---------------------------------------------------------------------------
  // L'Agent Console lit `canStartRecording` / `canStopRecording` avec la sémantique
  // « autorisé sauf interdiction explicite » (`!== false`, cf. CallComponent.js). Un profil qui
  // ne porte pas le champ autorise donc l'action : les interrupteurs partent à activé, et seule
  // une désactivation écrit `false`.
  const isRecordingAllowed = (field) => props.profile?.[field] !== false;

  const handleRecordingChange = (field, checked) => {
    props.setProfile({
      ...props.profile,
      [field]: checked
    });
  };

  // ✅ Validation
  const validateForm = () => {
    const newErrors = {};

    if (!props.profile?.displayName)
      newErrors.displayName = t("THIS_FIELD_IS_REQUIRED");
    if (!props.profile?.region)
      newErrors.region = t("THIS_FIELD_IS_REQUIRED");

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    props.setFormValid?.(isValid);
  };
  useEffect(() => {
    validateForm();
  }, [props.profile?.displayName, props.profile?.region]);

  const renderGeneralTab = () => (
    <>
      {/* --- Display Name --- */}
      <div className="agent-profile-form-group">
        <Field
          label={t("AGENT_PROFILE_NAME")}
          className="agent-profile-form-field"
          required
          validationState={errors.displayName ? "error" : "none"}
          validationMessage={errors.displayName}
        >
          <Input
            className="agent-profile-form-dropdown"
            placeholder={t("ENTER_NAME")}
            value={props.profile?.displayName || ""}
            onChange={(e, data) =>
              props.setProfile({
                ...props.profile,
                displayName: data.value,
              })
            }
          />
        </Field>
      </div>

      {/* --- Region --- */}
      <div className="agent-profile-form-group">
        <Field
          label={t("REGION")}
          className="agent-profile-form-field"
          required
          validationState={errors.region ? "error" : "none"}
          validationMessage={errors.region}
        >
          <Dropdown
            clearable
            className="agent-profile-form-dropdown"
            placeholder={t("SELECT_REGION")}
            value={props.profile?.region || ""}
            onOptionSelect={(_, data) => {
              props.setProfile({
                ...props.profile,
                region: data.optionValue,
              });
            }}
          >
            {REGIONS.map((region, index) => (
              <Option key={index} value={region.value}>
                {region.header}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      {/* --- Requeue Unanswered Transfered Call --- */}
      <div className="agent-profile-form-checkbox-row">
        <Label weight="semibold">
          {t("REQUEUE_UNASSWERED_TRANSFERED_CALL")}
          <InfoButton infoKey="REQUEUE_UNASSWERED_TRANSFERED_CALL" category="AGENTS" />
        </Label>
        <Switch
          checked={props?.profile?.RequeueUnansweredTransferedCall || false}
          onChange={(e, data) => {
            props.setProfile({
              ...props.profile,
              RequeueUnansweredTransferedCall: data.checked,
            });
          }}
        />
      </div>

    </>
  );

  /**
   * Éditeur du service d'envoi de SMS.
   *
   * ⚠️ CE BLOC CONTIENT DES SECRETS. L'URL porte la clé de fonction (`?code=…`) et les en-têtes
   * peuvent porter un jeton. `MyAgentProfile` l'exclut EXPLICITEMENT de la réponse envoyée à la
   * console — sans quoi il partirait dans le navigateur de chaque agent, où n'importe qui peut
   * le lire. Ne jamais réintroduire ce champ dans ce que la console reçoit.
   *
   * Le corps est un GABARIT, pas un corps figé : c'est la deuxième substitution de la chaîne.
   * La première produit le texte du SMS depuis un gabarit SMS, la seconde l'insère ici via
   * `{{message}}`. Les valeurs y sont échappées en JSON côté serveur — un guillemet dans un
   * message ne doit ni casser la requête ni pouvoir y ajouter un champ.
   */
  function renderSmsWebService() {
    const svc = props.profile?.SmsWebService || {};
    const setSvc = (changes) =>
      props.setProfile({
        ...props.profile,
        SmsWebService: { ...svc, ...changes },
      });

    const headers = Array.isArray(svc.headers) ? svc.headers : [];
    const setHeader = (i, changes) =>
      setSvc({ headers: headers.map((h, k) => (k === i ? { ...h, ...changes } : h)) });

    return (
      <div className="agent-profile-sms-service">
        <Label weight="semibold">{t("SMS_WEB_SERVICE")}</Label>
        <Text as="p" size={200}>{t("SMS_WEB_SERVICE_HINT")}</Text>

        <Field label={t("SMS_SERVICE_URL")} hint={t("SMS_SERVICE_URL_HINT")}>
          <Input
            disabled={readOnly}
            value={svc.url || ""}
            placeholder="https://…/api/sendSms?code=…"
            onChange={(_, d) => setSvc({ url: d.value })}
          />
        </Field>

        <Field label={t("SMS_SENDER_NUMBER")} hint={t("SMS_SENDER_NUMBER_HINT")}>
          <Input
            disabled={readOnly}
            value={svc.senderNumber || ""}
            placeholder="+33939206933"
            onChange={(_, d) => setSvc({ senderNumber: d.value })}
          />
        </Field>

        <Label weight="semibold">{t("SMS_SERVICE_HEADERS")}</Label>
        {headers.map((h, i) => (
          <div key={i} className="agent-profile-sms-header-row">
            <Input
              disabled={readOnly}
              value={h.name || ""}
              placeholder="Content-Type"
              onChange={(_, d) => setHeader(i, { name: d.value })}
            />
            <Input
              disabled={readOnly}
              value={h.value || ""}
              placeholder="application/json"
              onChange={(_, d) => setHeader(i, { value: d.value })}
            />
            <Button
              appearance="subtle"
              size="small"
              disabled={readOnly}
              onClick={() => setSvc({ headers: headers.filter((_, k) => k !== i) })}
            >
              ✕
            </Button>
          </div>
        ))}
        {!readOnly && (
          <Button
            appearance="subtle"
            size="small"
            onClick={() => setSvc({ headers: [...headers, { name: "", value: "" }] })}
          >
            + {t("SMS_ADD_HEADER")}
          </Button>
        )}

        <Field label={t("SMS_SERVICE_BODY")} hint={t("SMS_SERVICE_BODY_HINT")}>
          <textarea
            disabled={readOnly}
            rows={7}
            className="agent-profile-sms-body"
            value={svc.bodyTemplate || ""}
            placeholder={'{\n  "to": "{{callerNumber}}",\n  "from": "{{senderNumber}}",\n  "message": "{{message}}"\n}'}
            onChange={(e) => setSvc({ bodyTemplate: e.target.value })}
          />
        </Field>

        {/* Les mêmes marqueurs que les gabarits SMS, plus `{{message}}` qui porte le texte
            produit par le gabarit — c'est ce qui relie les deux étages. */}
        <Text as="p" size={200}>{t("AVAILABLE_VARIABLES")}</Text>
        <code className="agent-profile-sms-vars">
          {"{{message}} · {{callerNumber}} · {{senderNumber}} · {{customerName}} · {{agentName}} · {{queueName}} · {{companyName}}"}
        </code>
      </div>
    );
  }

  // ⚠️ Pas de `agent-profile-form-group` ici : cette classe est en `display:flex; flex-direction:row`,
  // elle alignerait le titre, l'aide et le tableau côte à côte et écraserait la colonne des noms.
  /**
   * Onglet SMS — Quick SMS.
   *
   * Sorti de l'onglet « Général » et posé à côté de Mail : un canal se configure sur son propre
   * onglet, pas au milieu des réglages d'appel. Les deux se lisent désormais de la même façon —
   * un interrupteur, puis ce qu'il révèle.
   *
   * ─── Ce qui se règle ici ────────────────────────────────────────────────────────────────────
   * Le DROIT d'envoyer, et le service qui envoie. Le droit vit sur le profil parce que c'est un
   * réglage d'équipe, accordé à une population ; il est REVÉRIFIÉ côté serveur à chaque envoi,
   * car masquer un onglet n'empêche personne de poster sur l'endpoint.
   *
   * ⚠️ Le bloc de service contient des SECRETS — l'URL porte la clé de fonction. Il est
   * explicitement exclu de la réponse de `MyAgentProfile`, qui recopie sinon TOUS les réglages du
   * profil dans le navigateur de chaque agent. Ne jamais l'y réintroduire.
   */
  const renderSmsTab = () => (
    <div className="agent-profile-mail-section">
      <div className="agent-profile-form-checkbox-row">
        <Label weight="semibold">
          {t("SMS_ENABLED")}
          <InfoButton infoKey="SMS_ENABLED" category="AGENTS" />
        </Label>
        <Switch
          checked={props?.profile?.SmsEnabled || false}
          disabled={readOnly}
          onChange={(e, data) => {
            props.setProfile({ ...props.profile, SmsEnabled: data.checked });
          }}
        />
      </div>

      {/* Masqué tant que le canal est fermé : quatre champs techniques affichés en permanence
          sur un profil qui n'envoie pas de SMS encombrent la page de tous les autres clients. */}
      {props?.profile?.SmsEnabled && renderSmsWebService()}
    </div>
  );

  /**
   * Onglet Mail — la participation de l'agent au canal courrier.
   *
   * ─── Ce qui se règle ici, et ce qui n'y est pas ─────────────────────────────────────────────
   * Les SLOTS (capacité) : combien de dossiers peuvent être posés en même temps sur le bureau
   * d'un agent portant ce profil. C'est la seule variable du routage mail — la file interroge
   * littéralement `c.mail.active < c.mail.capacity` pour décider d'attribuer.
   *
   * N'y sont PAS, et c'est délibéré : `active`, `activeItems`, `ownedOpenCases` — l'état vivant,
   * qui décrit les dossiers réellement posés à l'instant. Ni `state` (READY/PAUSED), que l'agent
   * pilote depuis sa console : mettre en pause est un geste de travail, pas un réglage
   * d'administration.
   *
   * ─── Pourquoi la capacité n'est pas un simple champ du profil ───────────────────────────────
   * La file ne lit pas le profil, elle lit le document AGENT. L'enregistrement propage donc la
   * valeur sur le bloc `mail` de chaque agent rattaché (`applyMailConfigToAgents`), en ne
   * touchant QUE `capacity` quand le bloc existe déjà : l'écraser remettrait `active` à zéro
   * alors que des dossiers y sont attribués, et la file en donnerait de nouveaux par-dessus.
   *
   * Désactiver le canal met la capacité à 0 plutôt que de supprimer le bloc : les dossiers en
   * cours restent visibles et traitables, aucun NOUVEAU n'est attribué. Les retirer d'un coup
   * les rendrait invisibles à l'agent qui les a en main.
   */
  const renderMailTab = () => {
    const mail = props.profile?.Mail || {};
    const setMail = (changes) =>
      props.setProfile({ ...props.profile, Mail: { ...mail, ...changes } });

    return (
      <div className="agent-profile-mail-section">
        <div className="agent-profile-form-checkbox-row">
          <Label weight="semibold">
            {t("MAIL_ENABLED")}
            <InfoButton infoKey="MAIL_ENABLED" category="AGENTS" />
          </Label>
          <Switch
            checked={mail.enabled === true}
            disabled={readOnly}
            onChange={(_, d) => setMail({ enabled: d.checked })}
          />
        </div>

        {mail.enabled === true && (
          <>
            <Field
              label={t("MAIL_CAPACITY")}
              hint={t("MAIL_CAPACITY_HINT")}
              validationState={mail.capacity !== undefined && Number(mail.capacity) < 1 ? "error" : "none"}
              validationMessage={mail.capacity !== undefined && Number(mail.capacity) < 1 ? t("MAIL_CAPACITY_MIN") : undefined}
            >
              <Input
                type="number"
                min={1}
                disabled={readOnly}
                className="agent-profile-form-dropdown"
                value={mail.capacity === undefined ? "" : String(mail.capacity)}
                onChange={(_, d) => setMail({ capacity: d.value === "" ? undefined : Number(d.value) })}
              />
            </Field>

            <Text as="p" size={200} className="agent-profile-mail-note">
              {t("MAIL_PROFILE_NOTE")}
            </Text>
          </>
        )}
      </div>
    );
  };

  const renderOboTab = () => (
    <div className="agent-profile-obo-section">
      <Label weight="semibold">{t("CALL_ON_BEHALF_OF")}</Label>
      <Text as="p" size={200} className="agent-profile-obo-hint">
        {t("SELECT_CALLFLOWS_FOR_OBO")}
      </Text>
      <Text as="p" size={200} className="agent-profile-obo-hint">
        {t("DEFAULT_FLOW_HINT")}
      </Text>

      {callFlowsArray.length === 0 ? (
        <Text as="p" size={300}>{t("NO_CALLFLOWS_AVAILABLE")}</Text>
      ) : (
        // Un seul RadioGroup pour toute la liste : le « par défaut » est un choix unique.
        <RadioGroup
          value={defaultFlowId || ""}
          onChange={(_, data) => handleDefaultFlowChange(data.value)}
        >
          <div className="agent-profile-obo-list">
            <div className="agent-profile-obo-row agent-profile-obo-header">
              <span className="agent-profile-obo-cell-name">{t("CALLFLOW")}</span>
              <span className="agent-profile-obo-cell-check">{t("SELECTED")}</span>
              <span className="agent-profile-obo-cell-default">{t("DEFAULT_FLOW")}</span>
            </div>

            {callFlowsArray.map((flow) => {
              const isSelected = selectedFlowIds.includes(flow.id);
              // Repli en cascade : le champ portant le nom varie selon l'origine de l'objet
              // (provisioning, agent IA…). Sans ça la cellule reste vide.
              const flowName = flow.displayName || flow.flowName || flow.name || flow.id;
              return (
                <div key={flow.id} className="agent-profile-obo-row">
                  <span className="agent-profile-obo-cell-name" title={flowName}>{flowName}</span>
                  <span className="agent-profile-obo-cell-check">
                    <Checkbox
                      checked={isSelected}
                      onChange={(_, data) => handleFlowToggle(flow.id, data.checked)}
                      aria-label={flowName}
                    />
                  </span>
                  <span className="agent-profile-obo-cell-default">
                    {/* Un flux non coché ne peut pas être le défaut. */}
                    <Radio value={flow.id} disabled={!isSelected} label="" />
                  </span>
                </div>
              );
            })}
          </div>
        </RadioGroup>
      )}
    </div>
  );

  const renderRecordingTab = () => (
    <>
      <Text as="p" size={200} className="agent-profile-obo-hint">
        {t("RECORDING_PERMISSIONS_HINT")}
      </Text>

      <div className="agent-profile-form-checkbox-row">
        <Label weight="semibold">{t("CAN_START_RECORDING")}</Label>
        <Switch
          checked={isRecordingAllowed("canStartRecording")}
          onChange={(_, data) => handleRecordingChange("canStartRecording", data.checked)}
        />
      </div>

      <div className="agent-profile-form-checkbox-row">
        <Label weight="semibold">{t("CAN_STOP_RECORDING")}</Label>
        <Switch
          checked={isRecordingAllowed("canStopRecording")}
          onChange={(_, data) => handleRecordingChange("canStopRecording", data.checked)}
        />
      </div>
    </>
  );

  return (
    <div className="agent-profile-form-root">
      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => setSelectedTab(data.value)}
        className="tab-list"
      >
        <Tab value="general">{t("GENERAL")}</Tab>
        <Tab value="obo">{t("CALL_ON_BEHALF_OF")}</Tab>
        <Tab value="recording">{t("RECORDING")}</Tab>
        <Tab value="mail">{t("MAIL")}</Tab>
        <Tab value="sms">{t("SMS")}</Tab>
      </TabList>

      {/* ⚠️ Le fieldset n'enveloppe QUE le contenu des onglets, jamais la TabList.
          `<fieldset disabled>` désactive tous les boutons qu'il contient — et un onglet EST un
          bouton. Envelopper le formulaire entier rendait la navigation impossible : on consultait
          un profil sans pouvoir changer d'onglet. Un contrôle qui NAVIGUE n'est pas un contrôle
          qui MODIFIE, et la lecture seule ne doit toucher que le second. */}
      <div className="agent-profile-form-tab-content">
        <ReadOnlyFieldset readOnly={readOnly}>
          {selectedTab === "general" && renderGeneralTab()}
          {selectedTab === "obo" && renderOboTab()}
          {selectedTab === "recording" && renderRecordingTab()}
          {selectedTab === "mail" && renderMailTab()}
          {selectedTab === "sms" && renderSmsTab()}
        </ReadOnlyFieldset>
      </div>
    </div>
  );
};

export default AgentProfileForm;
